import { AxiosMethods, QuotesRFQApi } from '@setvi/shared/enums';
import { axiosHelper } from '@setvi/shared/services';
import { QuoteDTO } from 'pages/quotes/types';
import {
  RenewQuoteParams,
  UpdatePreviewInfoBody,
  UpdateQuoteItemParams
} from './types';

export enum QuotesMutationKeys {
  CREATE_QUOTE = 'create-quote',
  UPDATE_QUOTE = 'update-quote',
  EXPORT_PDF = 'export-quote-pdf',
  UPDATE_PHOTO = 'update-quote-photo',
  UPDATE_PREVIEW_INFO = 'update-preview-info',
  UPDATE_QUOTE_ITEM = 'update-quote-item',
  UPDATE_QUOTE_STATUS = 'update_quote_status',
  RENEW_QUOTE = 'renew_quote',
  DELETE_QUOTE = 'delete-quote'
}

export const createQuoteMutation = () => ({
  mutationKey: [QuotesMutationKeys.CREATE_QUOTE],
  mutationFn: (body: Partial<QuoteDTO>) =>
    axiosHelper({
      endpoint: QuotesRFQApi.Create,
      method: AxiosMethods.POST,
      baseURL: process.env.REVOPS_API,
      body
    }),
  retry: false
});

export const updateQuoteMutation = () => ({
  mutationKey: [QuotesMutationKeys.UPDATE_QUOTE],
  mutationFn: ({ _id, ...rest }: Partial<QuoteDTO>) =>
    axiosHelper({
      endpoint: QuotesRFQApi.Update.replace(':quoteId', _id?.toString()),
      method: AxiosMethods.PATCH,
      baseURL: process.env.REVOPS_API,
      body: rest
    })
});

export const updateQuotePreviewInfoMutation = () => ({
  mutationKey: [QuotesMutationKeys.UPDATE_PREVIEW_INFO],
  mutationFn: ({ id, ...rest }: UpdatePreviewInfoBody) =>
    axiosHelper({
      endpoint: QuotesRFQApi.UpdatePreviewInfo.replace(
        ':quoteId',
        id?.toString()
      ),
      method: AxiosMethods.POST,
      baseURL: process.env.REVOPS_API,
      body: rest
    })
});

export const getExportedQuoteMutation = () => ({
  mutationKey: [QuotesMutationKeys.EXPORT_PDF],
  mutationFn: (id: string | number) =>
    axiosHelper({
      endpoint: QuotesRFQApi.ExportPDF.replace(':quoteId', id?.toString()),
      method: AxiosMethods.GET,
      baseURL: process.env.REVOPS_API,
      responseType: 'blob', // Treat response as a binary file
      headers: {
        Accept: 'text/plain', // Expect CSV response
        'content-disposition': 'attachment'
      }
    })
});

export const updateQuoteUserPhotoMutation = () => ({
  mutationKey: [QuotesMutationKeys.UPDATE_PHOTO],
  mutationFn: ({ id, image }: { id: string | number; image: File }) => {
    const formData = new FormData();
    formData.append('image', image);

    return axiosHelper({
      endpoint: QuotesRFQApi.UpdateProfilePhoto.replace(
        ':quoteId',
        id?.toString()
      ),
      method: AxiosMethods.POST,
      baseURL: process.env.REVOPS_API,
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }
});

export const updateQuoteItemMutation = () => ({
  mutationKey: [QuotesMutationKeys.UPDATE_QUOTE_ITEM],
  mutationFn: ({ quoteId, body }: UpdateQuoteItemParams) =>
    axiosHelper({
      endpoint: QuotesRFQApi.UpdateItem.replace(
        ':quoteId',
        quoteId?.toString()
      ),
      method: AxiosMethods.PATCH,
      baseURL: process.env.REVOPS_API,
      body
    })
});

export const updateQuoteStatusMutation = () => ({
  mutationKey: [QuotesMutationKeys.UPDATE_QUOTE_STATUS],
  mutationFn: ({ quoteId, statusId }: { quoteId: string; statusId: string }) =>
    axiosHelper({
      endpoint: QuotesRFQApi.UpdateQuoteStatus.replace(
        ':quoteId',
        quoteId?.toString()
      ),
      method: AxiosMethods.PUT,
      baseURL: process.env.REVOPS_API,
      body: { statusId }
    })
});

export const renewQuoteMutation = () => ({
  mutationKey: [QuotesMutationKeys.RENEW_QUOTE],
  mutationFn: ({ quoteId, ...rest }: RenewQuoteParams) =>
    axiosHelper({
      endpoint: QuotesRFQApi.RenewQuotes.replace(
        ':quoteId',
        quoteId?.toString()
      ),
      method: AxiosMethods.POST,
      baseURL: process.env.REVOPS_API,
      body: rest
    })
});

export const deleteQuoteMutation = () => ({
  mutationKey: [QuotesMutationKeys.DELETE_QUOTE],
  mutationFn: ({ quoteId }: { quoteId: string | number }) =>
    axiosHelper({
      endpoint: QuotesRFQApi.DeleteQuote.replace(
        ':quoteId',
        quoteId?.toString()
      ),
      method: AxiosMethods.DELETE,
      baseURL: process.env.REVOPS_API
    })
});
